import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DataGrid, { Column, FilterRow, Selection } from 'devextreme-react/data-grid';

interface GridConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (order: string[], visible: Record<string, boolean>, groupable: Record<string, boolean>, summary: Record<string, boolean>, fixed: Record<string, boolean>) => void;
  initialOrder: string[];
  initialVisible: Record<string, boolean>;
  initialGroupable: Record<string, boolean>;
  initialSummary: Record<string, boolean>;
  initialFixed: Record<string, boolean>;
  useLocalStorage: boolean;
  allColumns: any[];
  activeColumns: any[];
  allowGrouping?: boolean;
  allowSummary?: boolean;
}

const EXCLUDED_FIELDS = ['OID', 'NOVISIBLE'];

const GridConfigModal: React.FC<GridConfigModalProps> = ({
  open,
  onClose,
  onSave,
  initialOrder,
  initialVisible,
  initialGroupable,
  initialSummary,
  initialFixed,
  useLocalStorage,
  allColumns,
  activeColumns,
  allowGrouping = true,
  allowSummary = true,
}) => {
  const [localOrder, setLocalOrder] = useState<string[]>(initialOrder);
  const [localVisible, setLocalVisible] = useState<Record<string, boolean>>(initialVisible);
  const [localGroupable, setLocalGroupable] = useState<Record<string, boolean>>(initialGroupable);
  const [localSummary, setLocalSummary] = useState<Record<string, boolean>>(initialSummary);
  const [localFixed, setLocalFixed] = useState<Record<string, boolean>>(initialFixed);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setLocalOrder(initialOrder);
      setLocalVisible(initialVisible);
      setLocalGroupable(initialGroupable);
      setLocalSummary(initialSummary);
      setLocalFixed(initialFixed);
      setSelectedRowKeys([]);
    }
  }, [open, initialOrder, initialVisible, initialGroupable, initialSummary, initialFixed]);

  const handleMoveUp = () => {
    const selectedField = selectedRowKeys[0];
    if (!selectedField) return;
    const index = localOrder.findIndex(f => f === selectedField);
    if (index <= 0) return;
    const newOrder = [...localOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalOrder(newOrder);
    setSelectedRowKeys([selectedField]);
  };

  const handleMoveDown = () => {
    const selectedField = selectedRowKeys[0];
    if (!selectedField) return;
    const index = localOrder.findIndex(f => f === selectedField);
    if (index === -1 || index === localOrder.length - 1) return;
    const newOrder = [...localOrder];
    [newOrder[index + 1], newOrder[index]] = [newOrder[index], newOrder[index + 1]];
    setLocalOrder(newOrder);
    setSelectedRowKeys([selectedField]);
  };

  const handleSave = () => {
    onSave(localOrder, localVisible, localGroupable, localSummary, localFixed);
  };

  const currentColumns = useLocalStorage ? allColumns : activeColumns;

  const gridData = localOrder
    .filter(field => !EXCLUDED_FIELDS.includes(field))
    .map(field => {
      const col = currentColumns.find(c => c.dataField === field);
      return {
        field,
        caption: col?.caption || field || '-',
        isNumeric: col?.dataType === 'number',
        visible: localVisible[field] ?? true,
        groupable: localGroupable[field] || false,
        summary: localSummary[field] || false,
        fixed: localFixed[field] || false,
      };
    });

  const selectedField = selectedRowKeys[0];
  const selectedIndex = selectedField ? localOrder.findIndex(f => f === selectedField) : -1;
  const isUpDisabled = !selectedField || selectedIndex <= 0;
  const isDownDisabled = !selectedField || selectedIndex === -1 || selectedIndex === localOrder.length - 1;

  const gridStyles = `
    .compact-config-grid .dx-datagrid .dx-row > td {
      padding: 4px 6px !important;
    }
    .compact-config-grid .dx-datagrid .dx-header-row > td {
      padding: 8px 6px !important;
    }
    .compact-config-grid .dx-checkbox {
      transform: scale(0.85);
    }
    .compact-config-grid .dx-button.dx-button-small {
      padding: 2px;
    }
  `;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Configuración de columnas</DialogTitle>
      <DialogContent style={{ height: '80vh', padding: '8px 24px' }}>
        <div className="compact-config-grid" style={{ height: '100%', width: '100%' }}>
          <style>{gridStyles}</style>
          <div style={{ display: 'flex', height: '100%', gap: '8px' }}>
            {/* Contenedor de la grilla (ocupa todo el espacio restante) */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <DataGrid
                dataSource={gridData}
                keyExpr="field"
                showBorders
                width="100%"
                height="100%"
                columnAutoWidth
                scrolling={{ mode: 'virtual' }}
                rowAlternationEnabled={false}
                hoverStateEnabled={false}
                selectedRowKeys={selectedRowKeys}
                onSelectionChanged={(e) => setSelectedRowKeys(e.selectedRowKeys)}
              >
                <FilterRow visible={true} />
                <Selection mode="single"/>
                <Column dataField="caption" caption="Nombre de columna" width={200} allowFiltering={true} />
                <Column
                  caption="Visualizar"
                  alignment="center"
                  width={allowGrouping ? 80 : 250}
                  allowFiltering={false}
                  cellRender={(cellData: any) => (
                    <Checkbox
                      checked={cellData.data.visible}
                      onChange={e => {
                        const newVisible = { ...localVisible, [cellData.data.field]: e.target.checked };
                        setLocalVisible(newVisible);
                      }}
                      size="small"
                      style={{ padding: 0 }}
                    />
                  )}
                />
                {allowGrouping && <Column
                  caption="Permitir agrupación"
                  alignment="center"
                  width={140}
                  allowFiltering={false}
                  cellRender={(cellData: any) => (
                    <Checkbox
                      checked={cellData.data.groupable}
                      onChange={e => {
                        const newGroupable = { ...localGroupable, [cellData.data.field]: e.target.checked };
                        setLocalGroupable(newGroupable);
                      }}
                      disabled={EXCLUDED_FIELDS.includes(cellData.data.field)}
                      size="small"
                      style={{ padding: 0 }}
                    />
                  )}
                />}
                {allowSummary && <Column
                  caption="Permitir suma"
                  alignment="center"
                  width={allowGrouping ? 120 : 250}
                  allowFiltering={false}
                  cellRender={(cellData: any) =>
                    cellData.data.isNumeric ? (
                      <Checkbox
                        checked={cellData.data.summary}
                        onChange={e => {
                          const newSummary = { ...localSummary, [cellData.data.field]: e.target.checked };
                          setLocalSummary(newSummary);
                        }}
                        size="small"
                        style={{ padding: 0 }}
                      />
                    ) : null
                  }
                />}
                <Column
                  caption="Fijar a la izquierda"
                  alignment="center"
                  width={80}
                  allowFiltering={false}
                  cellRender={(cellData: any) => (
                    <Checkbox
                      checked={cellData.data.fixed}
                      onChange={e => {
                        const newFixed = { ...localFixed, [cellData.data.field]: e.target.checked };
                        setLocalFixed(newFixed);
                      }}
                      size="small"
                      style={{ padding: 0 }}
                    />
                  )}
                />
              </DataGrid>
            </div>
            {/* Panel de botones al costado derecho */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '8px', paddingTop: '8px' }}>
              <IconButton onClick={handleMoveUp} disabled={isUpDisabled} size="small" title="Subir fila seleccionada">
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleMoveDown} disabled={isDownDisabled} size="small" title="Bajar fila seleccionada">
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained">
          Guardar cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GridConfigModal;